import { useEffect, useRef, useState } from "react";
import { api, describeError } from "../lib/api";
import { safeNext } from "../lib/returnTo";
import { Wordmark } from "../components/Wordmark";
import { AlertIcon, CheckIcon } from "../components/Icons";
import { Spinner } from "../components/ui";
import { signIn } from "@pagemates/shared/copy";

const txt = signIn.emailLinks;

/*
 * Where the buttons in our emails land (PRD "Email addresses and email sign-in"). Both post the code from
 * the link once the page runs, so a mail scanner that only fetches the link changes nothing, and the code
 * is used in whichever browser the link opened.
 */

const params = () => new URLSearchParams(window.location.search);

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <main className="page landing email-link">
      <a href="/" className="signin-wm" aria-label={txt.pagematesHome}><Wordmark size="md" /></a>
      <section className="card link-card">{children}</section>
    </main>
  );
}

/** /signin/code?email=…&code=…&next=… : the "Sign in" button in a code email. */
export function SignInLinkPage() {
  const [state, setState] = useState<"working" | "failed">("working");
  const [err, setErr] = useState<string | null>(null);
  const ran = useRef(false);
  const q = params();
  const email = q.get("email") ?? "", next = safeNext(q.get("next"));
  useEffect(() => {
    if (ran.current) return; // once, even in StrictMode
    ran.current = true;
    void api.emailCodeVerify(email, q.get("code") ?? "")
      .then(() => window.location.replace(next))
      .catch((e: unknown) => { setErr(describeError(e)); setState("failed"); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (state === "working") return (
    <Frame><Spinner label={txt.signingYouIn} /><h1>{txt.signingYouIn}</h1><p className="muted small">{txt.asInThisBrowser(email)}</p></Frame>
  );
  return (
    <Frame>
      <span className="link-badge bad"><AlertIcon width={22} height={22} /></span>
      <h1>{txt.thisLinkHasBeen}</h1>
      <p className="muted small">{err ?? txt.codesWorkOnceFor}</p>
      <a className="btn primary" href={`/signin?${new URLSearchParams({ next, email })}`}>{txt.sendANewCode}</a>
    </Frame>
  );
}

/** /confirm-email?id=…&code=… : the "Confirm this address" button. Works without being signed in. */
export function ConfirmEmailPage() {
  const [state, setState] = useState<"working" | "done" | "failed">("working");
  const [err, setErr] = useState<string | null>(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const q = params();
    void api.confirmEmailLink(q.get("id") ?? "", q.get("code") ?? "")
      .then(() => setState("done"))
      .catch((e: unknown) => { setErr(describeError(e)); setState("failed"); });
  }, []);
  if (state === "working") return <Frame><Spinner label={txt.confirming} /><h1>{txt.confirmingYourAddress}</h1></Frame>;
  if (state === "done") return (
    <Frame>
      <span className="link-badge ok"><CheckIcon width={22} height={22} /></span>
      <h1>{txt.addressConfirmed}</h1>
      <p className="muted small">{txt.itCanSignYou}</p>
      <a className="btn primary" href="/me/settings#notifications">{txt.openPagemates}</a>
    </Frame>
  );
  return (
    <Frame>
      <span className="link-badge bad"><AlertIcon width={22} height={22} /></span>
      <h1>{txt.thatLinkDidnT}</h1>
      <p className="muted small">{txt.youCanSendA(err ?? txt.itMayHaveBeen)}</p>
      <a className="btn" href="/me/settings#notifications">{txt.goToSettings}</a>
    </Frame>
  );
}
